import {
  Redirect,
  Slot,
  useGlobalSearchParams,
  usePathname,
  useRouter,
} from 'expo-router';
import { useCallback, useEffect, useRef } from 'react';
import { View } from 'react-native';

import { useAdmin } from '@/api/partners/admins/use-admin';
import { useCompleteOwnerOnboarding } from '@/api/partners/admins/use-complete-owner-onboarding';
import { DownloadAppModal } from '@/components/download-app-modal';
import { OwnerOnboarding } from '@/components/owner-onboarding';
import { useAuth } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';

export default function PartnerLayout() {
  const pathname = usePathname();
  const params = useGlobalSearchParams();
  const router = useRouter();
  const status = useAuth.use.status();
  const isLogin = pathname.includes('/partner/login');
  const isAddCar = pathname === '/partner/cars';
  const replay = params.onboarding === '1';
  const { data: admin, isPending } = useAdmin({
    enabled: status === 'signIn' && !isLogin,
  });
  const { mutateAsync: completeOwnerOnboarding } = useCompleteOwnerOnboarding();
  const finishing = useRef(false);
  const hasOpenedStory = useRef(false);
  const trackedThisOpen = useRef(false);

  const waitingForAdmin =
    status === 'signIn' && !isLogin && isPending && admin == null;
  const forced =
    status === 'signIn' &&
    !isLogin &&
    admin?.ownerOnboardingCompleted === false;
  const showStory = forced || (replay && !isLogin);

  useEffect(() => {
    if (!showStory) {
      trackedThisOpen.current = false;
      finishing.current = false;
      return;
    }

    if (trackedThisOpen.current) return;

    trackedThisOpen.current = true;
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Partner Onboarding',
      'Is Return': hasOpenedStory.current,
    });
    hasOpenedStory.current = true;
  }, [showStory]);

  const finish = useCallback(async () => {
    if (finishing.current) return;

    finishing.current = true;

    try {
      await completeOwnerOnboarding();
    } catch {
      finishing.current = false;
      return;
    }

    if (replay) {
      router.setParams({ onboarding: undefined });
    }
  }, [completeOwnerOnboarding, replay, router]);

  return (
    <View style={{ flex: 1 }}>
      {waitingForAdmin ? null : forced && !isAddCar ? (
        <Redirect href="/partner/cars" />
      ) : (
        <Slot />
      )}

      {!isLogin && <DownloadAppModal />}
      {showStory ? <OwnerOnboarding onComplete={finish} /> : null}
    </View>
  );
}
